import { gql } from "@apollo/client";
import { SUPPORTED_CHAIN_IDS } from "config/chains";
import { BigNumberish, ethers } from "ethers";
import { BN_ZERO } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { getReferralsGraphClient } from "lib/subgraph";
import { useEffect, useState } from "react";
import {
  AffiliateTotalStats,
  CodeOwnershipInfo,
  RebateDistribution,
  RebateDistributionType,
  ReferralCodeStats,
  ReferralsStats,
  TotalReferralsStats,
  TraderReferralTotalStats,
} from "../types";
import { decodeReferralCode } from "../utils";
import { getCodeOwnersData } from "./useUserCodesOnAllChain";
import { bigNumberify } from "lib/numbers";

import { BSC } from "config/chains";
const ACTIVE_CHAINS = [BSC];
const DISTRIBUTION_TYPE_REBATES = "1";
const DISTRIBUTION_TYPE_DISCOUNT = "2";

const REFERRALS_GQL = gql`
  query referralData($typeIds: [String!]!, $account: String!, $referralTotalStatsId: String!) {
    distributions(
      first: 1000
      orderBy: timestamp
      orderDirection: desc
      where: { receiver: $account, typeId_in: $typeIds }
    ) {
      receiver
      typeId
      receiver
      markets
      tokens
      amounts
      amountsInUsd
      transactionHash
      timestamp
      id
    }
    affiliateStats: affiliateStats(
      first: 1000
      orderBy: volume
      orderDirection: desc
      where: { period: total, affiliate: $account }
    ) {
      referralCode
      trades
      tradedReferralsCount
      registeredReferralsCount
      volume
      totalRebateUsd
      discountUsd
      v1Data {
        volume
        discountUsd
        totalRebateUsd
      }
      v2Data {
        volume
        totalRebateUsd
        discountUsd
      }
    }
    referralCodes(first: 1000, where: { owner: $account }) {
      code
    }
    referralTotalStats: referralStat(id: $referralTotalStatsId) {
      volume
      discountUsd
      v1Data {
        volume
        discountUsd
      }
      v2Data {
        volume
        discountUsd
      }
    }
    affiliateTierInfo: affiliate(id: $account) {
      tierId
      id
      discountShare
    }
  }
`;

// export function useReferralsData(account?: string | null) {
//   const [data, setData] = useState<TotalReferralsStats>();
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     if (!account) {
//       setLoading(false);
//       return;
//     }

//     setLoading(true);

//     async function getChainReferralData(chainId: number): Promise<ReferralsStats | undefined> {
//       const graphClient = getReferralsGraphClient(chainId);
//       if (!graphClient) return undefined;

//       return getReferralsGraphClient(chainId)
//         .query({
//           query: REFERRALS_GQL,
//           variables: {
//             typeIds: Object.values(RebateDistributionType),
//             account: (account || "").toLowerCase(),
//             referralTotalStatsId: account && `total:0:${account.toLowerCase()}`,
//           },
//           fetchPolicy: "no-cache",
//         })
//         .then(async (res) => {
//           const affiliateDistributions: RebateDistribution[] = [];
//           const traderDistributions: RebateDistribution[] = [];

//           res.data.distributions.forEach((d) => {
//             const item = {
//               typeId: d.typeId,
//               receiver: ethers.getAddress(d.receiver),
//               markets: d.markets.map((market) => ethers.getAddress(market)),
//               tokens: d.tokens.map((token) => ethers.getAddress(token)),
//               amounts: d.amounts.map((a) => BigInt(a)),
//               amountsInUsd: d.amountsInUsd.map((a) => BigInt(a)),
//               timestamp: parseInt(d.timestamp),
//               transactionHash: d.transactionHash,
//               id: d.id,
//             };
//             if (d.typeId === RebateDistributionType.Rebate || d.typeId === RebateDistributionType.Claim) {
//               affiliateDistributions.push(item);
//             } else {
//               traderDistributions.push(item);
//             }
//           });

//           function getAffiliateRebateUsd(data: { totalRebateUsd: BigNumberish; discountUsd: BigNumberish }) {
//             return BigInt(data.totalRebateUsd) - BigInt(data.discountUsd);
//           }

//           //#region Code Ownership Info
//           // Getting all owners of the referral codes on other chains

//           const referralCodes = res.data.referralCodes.map((e) => e.code);
//           const allCodesOwnersOnOtherChains = await Promise.allSettled(
//             SUPPORTED_CHAIN_IDS.filter((otherChainId) => otherChainId !== chainId).map(async (otherChainId) => ({
//               chainId: otherChainId,
//               data: await getCodeOwnersData(otherChainId, account, referralCodes),
//             }))
//           ).then((allChainsResult) =>
//             allChainsResult
//               .filter(
//                 (
//                   chainResult
//                 ): chainResult is PromiseFulfilledResult<{
//                   chainId: number;
//                   data: CodeOwnershipInfo[] | undefined;
//                 }> => chainResult.status === "fulfilled"
//               )
//               .map((r) => r.value)
//           );

//           const allOwnersOnOtherChainsMap: {
//             [code: string]: {
//               [chainId: number]: CodeOwnershipInfo;
//             };
//           } = {};
//           for (const chainCodesOwners of allCodesOwnersOnOtherChains) {
//             for (const codeOwnershipInfo of chainCodesOwners.data || EMPTY_ARRAY) {
//               if (!allOwnersOnOtherChainsMap[codeOwnershipInfo.code]) {
//                 allOwnersOnOtherChainsMap[codeOwnershipInfo.code] = {};
//               }

//               allOwnersOnOtherChainsMap[codeOwnershipInfo.code][chainCodesOwners.chainId] = codeOwnershipInfo;
//             }
//           }
//           //#endregion

//           const affiliateReferralCodesStats: ReferralCodeStats[] = res.data.affiliateStats
//             .filter((e) => {
//               return res.data.referralCodes.some((c) => c.code === e.referralCode);
//             })
//             .map(
//               (e): ReferralCodeStats => ({
//                 referralCode: decodeReferralCode(e.referralCode),
//                 trades: parseInt(e.trades),
//                 tradedReferralsCount: parseInt(e.tradedReferralsCount),
//                 registeredReferralsCount: parseInt(e.registeredReferralsCount),
//                 allOwnersOnOtherChains: allOwnersOnOtherChainsMap[e.referralCode],
//                 volume: BigInt(e.volume),
//                 totalRebateUsd: BigInt(e.totalRebateUsd),
//                 affiliateRebateUsd: getAffiliateRebateUsd(e),
//                 discountUsd: BigInt(e.discountUsd),
//                 v1Data: {
//                   volume: BigInt(e.v1Data.volume),
//                   totalRebateUsd: BigInt(e.v1Data.totalRebateUsd),
//                   discountUsd: BigInt(e.v1Data.discountUsd),
//                   affiliateRebateUsd: getAffiliateRebateUsd(e.v1Data),
//                 },
//                 v2Data: {
//                   volume: BigInt(e.v2Data.volume),
//                   totalRebateUsd: BigInt(e.v2Data.totalRebateUsd),
//                   discountUsd: BigInt(e.v2Data.discountUsd),
//                   affiliateRebateUsd: getAffiliateRebateUsd(e.v2Data),
//                 },
//               })
//             );

//           const affiliateTotalStats: AffiliateTotalStats = res.data.affiliateStats.reduce(
//             (acc: AffiliateTotalStats, cv) => {
//               acc.trades = acc.trades + parseInt(cv.trades);
//               acc.tradedReferralsCount = acc.tradedReferralsCount + parseInt(cv.tradedReferralsCount);
//               acc.registeredReferralsCount = acc.registeredReferralsCount + parseInt(cv.registeredReferralsCount);

//               acc.totalRebateUsd = acc.totalRebateUsd + BigInt(cv.totalRebateUsd);
//               acc.volume = acc.volume + BigInt(cv.volume);
//               acc.discountUsd = acc.discountUsd + BigInt(cv.discountUsd);
//               acc.affiliateRebateUsd = acc.affiliateRebateUsd + getAffiliateRebateUsd(cv);

//               acc.v1Data.volume = acc.v1Data.volume + BigInt(cv.v1Data.volume);
//               acc.v1Data.totalRebateUsd = acc.v1Data.totalRebateUsd + BigInt(cv.v1Data.totalRebateUsd);
//               acc.v1Data.discountUsd = acc.v1Data.discountUsd + BigInt(cv.v1Data.discountUsd);
//               acc.v1Data.affiliateRebateUsd = acc.v1Data.affiliateRebateUsd + getAffiliateRebateUsd(cv.v1Data);

//               acc.v2Data.volume = acc.v2Data.volume + BigInt(cv.v2Data.volume);
//               acc.v2Data.totalRebateUsd = acc.v2Data.totalRebateUsd + BigInt(cv.v2Data.totalRebateUsd);
//               acc.v2Data.discountUsd = acc.v2Data.discountUsd + BigInt(cv.v2Data.discountUsd);
//               acc.v2Data.affiliateRebateUsd = acc.v2Data.affiliateRebateUsd + getAffiliateRebateUsd(cv.v2Data);
//               return acc;
//             },
//             {
//               trades: 0,
//               tradedReferralsCount: 0,
//               registeredReferralsCount: 0,
//               volume: BN_ZERO,
//               totalRebateUsd: BN_ZERO,
//               affiliateRebateUsd: BN_ZERO,
//               discountUsd: BN_ZERO,
//               v1Data: {
//                 volume: BN_ZERO,
//                 totalRebateUsd: BN_ZERO,
//                 affiliateRebateUsd: BN_ZERO,
//                 discountUsd: BN_ZERO,
//               },
//               v2Data: {
//                 volume: BN_ZERO,
//                 totalRebateUsd: BN_ZERO,
//                 affiliateRebateUsd: BN_ZERO,
//                 discountUsd: BN_ZERO,
//               },
//             } as AffiliateTotalStats
//           );

//           const traderReferralTotalStats: TraderReferralTotalStats = res.data.referralTotalStats
//             ? {
//                 volume: BigInt(res.data.referralTotalStats.volume)!,
//                 discountUsd: BigInt(res.data.referralTotalStats.discountUsd)!,
//                 v1Data: {
//                   volume: BigInt(res.data.referralTotalStats.v1Data.volume)!,
//                   discountUsd: BigInt(res.data.referralTotalStats.v1Data.discountUsd)!,
//                 },
//                 v2Data: {
//                   volume: BigInt(res.data.referralTotalStats.v2Data.volume)!,
//                   discountUsd: BigInt(res.data.referralTotalStats.v2Data.discountUsd)!,
//                 },
//               }
//             : {
//                 volume: BN_ZERO,
//                 discountUsd: BN_ZERO,
//                 v1Data: {
//                   volume: BN_ZERO,
//                   discountUsd: BN_ZERO,
//                 },
//                 v2Data: {
//                   volume: BN_ZERO,
//                   discountUsd: BN_ZERO,
//                 },
//               };

//           return {
//             chainId,
//             affiliateDistributions,
//             traderDistributions,
//             affiliateReferralCodesStats,
//             affiliateTierInfo: res.data.affiliateTierInfo,
//             affiliateTotalStats,
//             traderReferralTotalStats,
//             codes: res.data.referralCodes.map((e) => decodeReferralCode(e.code)),
//           } as ReferralsStats;
//         });
//     }

//     Promise.allSettled(
//       SUPPORTED_CHAIN_IDS.map(async (chainId) => {
//         try {
//           const data = await getChainReferralData(chainId);
//           return data;
//         } catch (e) {
//           // eslint-disable-next-line no-console
//           console.error(e);
//           return undefined;
//         }
//       })
//     )
//       .then((allChainResults) =>
//         allChainResults
//           .filter<PromiseFulfilledResult<ReferralsStats>>(
//             (maybeFulfilledChainResult): maybeFulfilledChainResult is PromiseFulfilledResult<ReferralsStats> =>
//               maybeFulfilledChainResult.status === "fulfilled"
//           )
//           .map((fulfilledChainResult) => fulfilledChainResult.value)
//           .reduce(
//             (accumulator, currentValue) => {
//               if (!currentValue) return accumulator;
//               const { chainId } = currentValue;
//               accumulator.chains[chainId] = currentValue;
//               const { affiliateTotalStats, traderReferralTotalStats } = currentValue;

//               accumulator.total.registeredReferralsCount += affiliateTotalStats.registeredReferralsCount;
//               accumulator.total.affiliateVolume = accumulator.total.affiliateVolume + affiliateTotalStats.volume;
//               accumulator.total.affiliateRebateUsd =
//                 accumulator.total.affiliateRebateUsd + affiliateTotalStats.affiliateRebateUsd;

//               accumulator.total.discountUsd = accumulator.total.discountUsd + traderReferralTotalStats.discountUsd;
//               accumulator.total.traderVolume = accumulator.total.traderVolume + traderReferralTotalStats.volume;

//               return accumulator;
//             },
//             {
//               total: {
//                 registeredReferralsCount: 0,
//                 affiliateVolume: BN_ZERO,
//                 affiliateRebateUsd: BN_ZERO,
//                 discountUsd: BN_ZERO,
//                 traderVolume: BN_ZERO,
//               },
//               chains: {},
//             } as TotalReferralsStats
//           )
//       )
//       .then(setData)
//       // eslint-disable-next-line no-console
//       .catch(console.warn)
//       .finally(() => {
//         setLoading(false);
//       });
//   }, [setData, account]);

//   return {
//     data: data,
//     loading,
//   };
// }

export function useUserCodesOnAllChain(account) {
  const [data, setData] = useState<{ [chainId: number]: { [code: string]: any } } | null>(null);
  const query = gql`
    query referralCodesOnAllChain($account: String!) {
      referralCodes(first: 1000, where: { owner: $account }) {
        code
      }
    }
  `;
  useEffect(() => {
    async function main() {
      const [/*arbitrumCodes, avalancheCodes, */bscCodes] = await Promise.all(
        ACTIVE_CHAINS.map((chainId) => {
          return getReferralsGraphClient(chainId)
            .query({ query, variables: { account: (account || "").toLowerCase() } })
            .then(({ data }) => {
              return data.referralCodes.map((c) => c.code);
            });
        })
      );
      const [/*codeOwnersOnAvax = [], codeOwnersOnArbitrum = [],*/ codeOwnersOnBsc = []] = await Promise.all([
        // getCodeOwnersData(AVALANCHE, account, arbitrumCodes),
        // getCodeOwnersData(ARBITRUM, account, avalancheCodes),
        getCodeOwnersData(BSC, account, bscCodes),
      ]);

      setData({
        // [ARBITRUM]: codeOwnersOnAvax.reduce((acc, cv) => {
        //   acc[cv.code] = cv;
        //   return acc;
        // }, {}),
        // [AVALANCHE]: codeOwnersOnArbitrum.reduce((acc, cv) => {
        //   acc[cv.code] = cv;
        //   return acc;
        // }, {}),
        [BSC]: codeOwnersOnBsc.reduce((acc, cv) => {
          acc[cv.code] = cv;
          return acc;
        }, {}),
      });
    }

    main();
  }, [account, query]);
  return data;
}


export function useReferralsData(chainId, account) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const ownerOnOtherChain = useUserCodesOnAllChain(account);
  useEffect(() => {
    if (!chainId || !account) {
      setLoading(false);
      return;
    }
    const startOfDayTimestamp = Math.floor(Date.now() / 1000 / 86400) * 86400;
    const query = gql`
      query referralData($typeIds: [String!]!, $account: String!, $timestamp: Int!, $referralTotalStatsId: String!) {
        distributions(
          first: 1000
          orderBy: timestamp
          orderDirection: desc
          where: { receiver: $account, typeId_in: $typeIds }
        ) {
          receiver
          amount
          typeId
          token
          transactionHash
          timestamp
        }
        referrerTotalStats: referrerStats(
          first: 1000
          orderBy: volume
          orderDirection: desc
          where: { period: total, referrer: $account }
        ) {
          referralCode
          volume
          trades
          tradedReferralsCount
          registeredReferralsCount
          totalRebateUsd
          discountUsd
        }
        referrerLastDayStats: referrerStats(
          first: 1000
          where: { period: daily, referrer: $account, timestamp: $timestamp }
        ) {
          referralCode
          volume
          trades
          tradedReferralsCount
          registeredReferralsCount
          totalRebateUsd
          discountUsd
        }
        referralCodes(first: 1000, where: { owner: $account }) {
          code
        }
        referralTotalStats: referralStat(id: $referralTotalStatsId) {
          volume
          discountUsd
        }
        referrerTierInfo: referrer(id: $account) {
          tierId
          id
          discountShare
        }
      }
    `;
    setLoading(true);
    // let typeIds = [DISTRIBUTION_TYPE_REBATES, DISTRIBUTION_TYPE_DISCOUNT];
    // let account1 =  (account || "").toLowerCase();
    // let timestamp =  startOfDayTimestamp;
    // let referralTotalStatsId = account && `total:0:${account.toLowerCase()}`;

    getReferralsGraphClient(chainId)
      .query({
        query,
        variables: {
          typeIds: [DISTRIBUTION_TYPE_REBATES, DISTRIBUTION_TYPE_DISCOUNT],
          account: (account || "").toLowerCase(),
          timestamp: startOfDayTimestamp,
          referralTotalStatsId: account && `total:0:${account.toLowerCase()}`,
        },
      })
      .then((res) => {
        const rebateDistributions: any[] = [];
        const discountDistributions: any[] = [];
        console.log("eagle res = ", res)

        res.data.distributions.forEach((d) => {
          const item = {
            timestamp: parseInt(d.timestamp),
            transactionHash: d.transactionHash,
            receiver: ethers.getAddress(d.receiver),
            amount: bigNumberify(d.amount),
            typeId: d.typeId,
            token: ethers.getAddress(d.token),
          };
          if (d.typeId === DISTRIBUTION_TYPE_REBATES) {
            rebateDistributions.push(item);
          } else {
            discountDistributions.push(item);
          }
        });

        function prepareStatsItem(e) {
          return {
            volume: bigNumberify(e.volume),
            trades: parseInt(e.trades),
            tradedReferralsCount: parseInt(e.tradedReferralsCount),
            registeredReferralsCount: parseInt(e.registeredReferralsCount),
            totalRebateUsd: bigNumberify(e.totalRebateUsd),
            discountUsd: bigNumberify(e.discountUsd),
            referralCode: decodeReferralCode(e.referralCode),
            ownerOnOtherChain: ownerOnOtherChain?.[chainId][e.referralCode],
          };
        }

        function getCumulativeStats(data: any[] = []) {
          return data.reduce(
            (acc: {
              totalRebateUsd: any;
              volume: any;
              discountUsd: any;
              trades: number;
              tradedReferralsCount: number;
              registeredReferralsCount: number;
            }, cv) => {
              console.log("eagle acc.totalRebateUsd = ", acc.totalRebateUsd)
              console.log("eagle cv.totalRebateUsd) = ", cv.totalRebateUsd)
              acc.totalRebateUsd = acc.totalRebateUsd + cv.totalRebateUsd;
              acc.volume = acc.volume + cv.volume;
              acc.discountUsd = acc.discountUsd + cv.discountUsd;
              acc.trades = acc.trades + cv.trades;
              acc.tradedReferralsCount = acc.tradedReferralsCount + cv.tradedReferralsCount;
              acc.registeredReferralsCount = acc.registeredReferralsCount + cv.registeredReferralsCount;
              return acc;
            },
            {
              totalRebateUsd: bigNumberify(0),
              volume: bigNumberify(0),
              discountUsd: bigNumberify(0),
              trades: 0,
              tradedReferralsCount: 0,
              registeredReferralsCount: 0,
            }
          );
        }

        let referrerTotalStats = res.data.referrerTotalStats.map(prepareStatsItem);
        console.log("eagle referrerTotalStats = ", referrerTotalStats)
        setData({
          rebateDistributions,
          discountDistributions,
          referrerTotalStats,
          referrerTierInfo: res.data.referrerTierInfo,
          referrerLastDayStats: res.data.referrerLastDayStats.map(prepareStatsItem),
          cumulativeStats: getCumulativeStats(referrerTotalStats),
          codes: res.data.referralCodes.map((e) => decodeReferralCode(e.code)),
          referralTotalStats: res.data.referralTotalStats
            ? {
              volume: bigNumberify(res.data.referralTotalStats.volume),
              discountUsd: bigNumberify(res.data.referralTotalStats.discountUsd),
            }
            : {
              volume: bigNumberify(0),
              discountUsd: bigNumberify(0),
            },
        });
      })
      .catch((err) => {
        console.log("useREferralsData err = ", err)
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setData, chainId, account, ownerOnOtherChain]);

  return {
    data: data || null,
    loading,
  };
}
